import JSZip from 'jszip'
import { generateAccessControlScript } from '@/utils/iSpringAccessControl'

/**
 * Mixin for opening iSpring documents in a new browser tab
 * Extracts index.html from ZIP, injects proxy base URL, and opens in new window
 * Includes 2h10 access timer for security
 */
export default {
    data() {
        return {
            iSpringLoading: false
        }
    },

    methods: {
        /**
         * Open an iSpring document in a new browser tab
         * @param {Object} document - The document object with id and format
         * @returns {Promise<void>}
         */
        async openISpringDocument(document) {
            if (document.format !== 'ispring') {
                console.warn('Document is not iSpring format')
                return
            }

            this.iSpringLoading = true

            try {
                // Download the ZIP file as blob
                const response = await this.$axios.get(
                    `/api/documents/${document.id}/download`,
                    { responseType: 'blob' }
                )

                // Load the ZIP with JSZip
                const zip = await JSZip.loadAsync(response.data)
                const htmlFile = zip.file('index.html')

                if (!htmlFile) {
                    this.$store.commit('setSnack', {
                        type: 'red',
                        msg: 'Fichier iSpring invalide : index.html non trouvé'
                    })
                    return
                }

                // Extract and modify the HTML
                let htmlContent = await htmlFile.async('string')

                // Build the proxy URL
                const baseUrl = process.env.VUE_APP_API_URL + `/api/documents/${document.id}/proxy/`

                // Parse HTML and inject base tag
                const parser = new DOMParser()
                const doc = parser.parseFromString(htmlContent, 'text/html')
                const head = doc.getElementsByTagName('head')[0]

                if (head) {
                    // Create and inject base element at the beginning of head
                    const baseElement = window.document.createElement('base')
                    baseElement.setAttribute('href', baseUrl)
                    head.insertBefore(baseElement, head.firstChild)
                }

                // Injecter le script de contrôle d'accès (timer 2h10 local uniquement,
                // pas de check serveur : les documents de la bibliothèque ne sont pas
                // liés à un Support et n'ont pas de notion de séance)
                const accessControlScript = generateAccessControlScript({
                    apiUrl: process.env.VUE_APP_API_URL,
                    supportId: document.id,
                    type: 'document',
                    meetingId: null,
                    meetingEnd: null,
                    serverCheckEnabled: false
                })
                const scriptElement = window.document.createElement('script')
                scriptElement.textContent = accessControlScript
                doc.body.appendChild(scriptElement)

                // Open in new window
                const previewContent = doc.documentElement.outerHTML
                const previewWindow = window.open()

                if (previewWindow) {
                    previewWindow.document.write(previewContent)
                    previewWindow.document.close()
                } else {
                    this.$store.commit('setSnack', {
                        type: 'red',
                        msg: 'Impossible d\'ouvrir la fenêtre. Vérifiez les paramètres de votre navigateur (bloqueur de popup).'
                    })
                }

            } catch (error) {
                console.error('iSpring preview error:', error)
                this.$store.commit('setSnack', {
                    type: 'red',
                    msg: 'Erreur lors de l\'ouverture du document iSpring'
                })
            } finally {
                this.iSpringLoading = false
            }
        }
    }
}
